import {
  type EventStandardDefs,
  type FeatureStandardDefs,
  LANGUAGES,
  type LanguagesType,
} from '@solinkcorp/event-standard';
import { type LocaleDefinition } from '@solinkcorp/locale';
import {
  type AnyWidgetExceptTicker,
  CommonWidgetUtils,
  FIELD_NAME_EMPTY,
  WIDGET_DATA_UNIT_TYPE_QUANTITY,
  WIDGET_LEGACY_DATA_PATH_TRANSACTIONS_COUNT,
} from '@solinkcorp/vd-common';
import { ALWAYS_DISPLAY_TABS, DASHBOARD_TAB, type DashboardTab } from './constants';

export const shouldShowRatioWithSelect = (statistic: string) =>
  CommonWidgetUtils.isShowingPercentage(statistic) || CommonWidgetUtils.isShowingRatio(statistic);

const getUnitType = (eventStandard: EventStandardDefs, fieldName: string) => {
  return eventStandard[fieldName]?.unitType ?? WIDGET_DATA_UNIT_TYPE_QUANTITY;
};

export const canCalPercentage = (eventStandard: EventStandardDefs, dataPaths: string[]) =>
  getUnitType(eventStandard, dataPaths[0]) === getUnitType(eventStandard, dataPaths[1]);

export const isDivision = (statistic: string) =>
  CommonWidgetUtils.isShowingPercentage(statistic) || CommonWidgetUtils.isShowingRatio(statistic);

const getFieldSumName = (
  eventStandard: EventStandardDefs,
  fieldName: string,
  locale: LocaleDefinition,
) => {
  let name = fieldName;
  if (eventStandard[fieldName]) {
    const localeCode = locale.locale;
    const eventStandardLocale =
      eventStandard[fieldName].i18n?.[localeCode] ?? eventStandard[fieldName].i18n?.en;
    name = eventStandardLocale?.sum || eventStandardLocale?.fullName || fieldName;
  } else if (
    fieldName === FIELD_NAME_EMPTY ||
    fieldName === WIDGET_LEGACY_DATA_PATH_TRANSACTIONS_COUNT
  ) {
    name = locale.fieldName.fieldNameEmpty;
  }
  return name;
};

export const getDivisionExpression = (
  eventStandard: EventStandardDefs,
  dataPaths: string[],
  dataStatistic: string,
  locale: LocaleDefinition,
) => {
  const numeratorField = dataPaths[0];
  const denominatorField = dataPaths[1];
  const numeratorFieldName = getFieldSumName(eventStandard, numeratorField, locale);
  const denominatorFieldName = getFieldSumName(eventStandard, denominatorField, locale);
  const isDataPercentage = CommonWidgetUtils.isShowingPercentage(dataStatistic);
  return `${numeratorFieldName} / ${denominatorFieldName}${isDataPercentage ? ' * 100%' : ''}`;
};

/**
 * normalizeLocaleCode takes in a locale code, and if feature-standard supports it,
 * it returns the same locale code, otherwise it returns 'en'.
 */
export function normalizeLocaleCode(localeCode: string): LanguagesType {
  if (LANGUAGES.includes(localeCode as any)) {
    return localeCode as LanguagesType;
  }
  return 'en';
}

/**
 * Given an array of tags, returns an array of `{ value, label }` objects, where
 * the value and label are the translated name of the tag taken from feature-standard.
 */
export function getWidgetTagsOptions({
  featureStandard,
  localeCode,
  tags,
  options = {},
}: {
  featureStandard: FeatureStandardDefs;
  localeCode: string;
  tags: string[];
  options: {
    featureStandardTags?: FeatureStandardDefs['tags'];
  };
}) {
  const featureStandardTags = options.featureStandardTags || featureStandard.tags;
  const lc = normalizeLocaleCode(localeCode);
  const filteredTagsFeatureStandard = filterWidgetTagsNotInFeatureStandard(
    tags,
    featureStandardTags,
  );
  return filteredTagsFeatureStandard.flatMap((tagName) => {
    const tagInfo = featureStandardTags[tagName];
    return tagInfo
      ? [
          {
            value: tagName,
            label: tagInfo.i18n[lc].name,
          },
        ]
      : [
          {
            value: tagName,
            label: tagName,
          },
        ];
  });
}

/**
 * Tags NOT apart of feature standard are filtered out
 */
export function filterWidgetTagsNotInFeatureStandard(
  tags: string[],
  featureStandardTags: FeatureStandardDefs['tags'],
) {
  return tags.filter(
    (tag: string) =>
      featureStandardTags[tag] && featureStandardTags[tag].applicableTo === 'widgets',
  );
}

/**
 * Get localized tag name using Feature Standard
 * All does not exist in Feature Standard, so use locale in VD instead
 */
export const getLocalizedTagName = (
  featureStandard: FeatureStandardDefs,
  tag: DashboardTab,
  locale: LocaleDefinition,
) => {
  const lc = normalizeLocaleCode(locale.locale);
  if (tag === DASHBOARD_TAB.ALL) {
    return locale.general.all;
  } else if (featureStandard.tags[tag]) {
    return featureStandard.tags[tag].i18n[lc].name;
  }
  return tag;
};

/**
 * Create a string with a the localized tabs
 */
export const generateListOfTabsString = (
  featureStandard: FeatureStandardDefs,
  tabs: DashboardTab[],
  locale: LocaleDefinition,
) => {
  let listOfTabs = '';
  tabs.forEach((tab) => {
    const localizedTab = getLocalizedTagName(featureStandard, tab, locale);
    listOfTabs += listOfTabs ? `, ${localizedTab}` : localizedTab;
  });
  return listOfTabs;
};

/**
 * We mutated widgets with the following properties
 */
export interface MutatedAnyWidgetExceptTicker extends AnyWidgetExceptTicker {
  isHidden?: boolean;
}

/**
 * Generate a list of visible dashboards sorted based on CH widget metadata
 * For a tab to visible:
 * 1. There must be one widget with the corresponding tag
 * 2. Must be defined in CH widget metadata
 * 3. Tag must be defined in Feature Standard
 * The exception is the Main and All Tabs, they are not sorted and are always visible
 */
export function getVisibleDashboardTabs(
  featureStandard: FeatureStandardDefs,
  widgets: MutatedAnyWidgetExceptTicker[],
  widgetTags: string[],
) {
  const tabsWithWidgets: DashboardTab[] = [];

  /**
   * Generate list of tabs with widgets
   * widgets with the isHidden property are ignored
   */
  widgets.forEach((widget) => {
    widget.widgetSettings?.tags?.forEach((tag: string) => {
      if (!tabsWithWidgets.includes(tag) && !widget.isHidden) {
        tabsWithWidgets.push(tag);
      }
    });
  });

  /**
   * Remove tabs that don't have widgets
   * this list will also be ordered based on widget metadata
   */
  const visibleTabs = widgetTags.filter(
    (tab) =>
      (tabsWithWidgets.includes(tab) || ALWAYS_DISPLAY_TABS.includes(tab)) &&
      tab !== DASHBOARD_TAB.MAIN &&
      featureStandard.tags[tab] &&
      featureStandard.tags[tab].applicableTo === 'widgets',
  );

  /**
   * AI tab is always displayed to promote the feature
   * AI tab should always be the second tab since the first tab is Main
   */
  if (visibleTabs.includes(DASHBOARD_TAB.AI)) {
    const aiIndex = visibleTabs.indexOf(DASHBOARD_TAB.AI);
    visibleTabs.splice(aiIndex, 1);
    visibleTabs.splice(0, 0, DASHBOARD_TAB.AI);
  }

  return [DASHBOARD_TAB.MAIN, ...visibleTabs, DASHBOARD_TAB.ALL];
}

/**
 * Determine if the widget exists on the current tab
 * All widgets exist on the all tab
 * Widgets that are untagged will exist on main tab
 * Otherwise match the tag with the tab
 */
export function widgetExistsOnTab({ widgetSettings }: AnyWidgetExceptTicker, tab: DashboardTab) {
  if (tab === DASHBOARD_TAB.ALL || (tab === DASHBOARD_TAB.MAIN && !widgetSettings.tags?.length)) {
    return true;
  }
  return widgetSettings?.tags?.includes(tab);
}

/**
 * Determine if the current tab still has visible widgets
 */
export function hasVisibleWidgets(widgets: AnyWidgetExceptTicker[], tab: DashboardTab) {
  let hasVisibleWidgets = false;
  if (tab === DASHBOARD_TAB.MAIN || tab === DASHBOARD_TAB.ALL) {
    hasVisibleWidgets = true;
  }

  widgets.forEach(({ widgetSettings }) => {
    widgetSettings?.tags?.forEach((tag: DashboardTab) => {
      if (tab === tag) {
        hasVisibleWidgets = true;
      }
    });
  });

  return hasVisibleWidgets;
}

/**
 * Get all widgets for a particular tab
 * The Main tab will also include widgets that are not tagged
 */
export function getWidgetsByTab(widgets: AnyWidgetExceptTicker[], tab: DashboardTab) {
  const widgetsByTab: AnyWidgetExceptTicker[] = [];

  widgets.forEach((widget) => {
    const { widgetSettings } = widget;
    if (tab === DASHBOARD_TAB.MAIN && !widgetSettings.tags?.length) {
      widgetsByTab.push(widget);
    } else {
      widget.widgetSettings?.tags?.forEach((tag: DashboardTab) => {
        if (tab === tag) {
          widgetsByTab.push(widget);
        }
      });
    }
  });

  return widgetsByTab;
}

/**
 * Current API can't support sorting across multiple dasboard tabs
 * If Widgets appear in multiple tabs, this will leading to sorting for both
 * Generate a list of tabs that have been sorted accidentally to display a warning message
 */
export function getUnintentedSortedTabs({
  prevWidgets,
  widgets,
  dashboardTab,
  visibleDashboardTabs,
}: {
  prevWidgets: AnyWidgetExceptTicker[];
  widgets: AnyWidgetExceptTicker[];
  dashboardTab: DashboardTab;
  visibleDashboardTabs: DashboardTab[];
}) {
  const unintentedSortedTabs: DashboardTab[] = [];

  visibleDashboardTabs.forEach((tab) => {
    const widgetsByTab = getWidgetsByTab(widgets, tab);
    const prevWidgetsByTab = getWidgetsByTab(prevWidgets, tab);
    if (JSON.stringify(widgetsByTab) !== JSON.stringify(prevWidgetsByTab) && tab !== dashboardTab) {
      unintentedSortedTabs.push(tab);
    }
  });

  return unintentedSortedTabs;
}
